// Service for determining and managing user locale

class LocaleService {
    private userLocale: string | null = null;

    /**
     * Get user locale
     * Determination order:
     * 1. From user settings (if saved)
     * 2. From localStorage (if saved)
     * 3. From browser (navigator.language)
     * 4. Default (en)
     */
    getUserLocale(): string {
        // If locale is already determined, return it
        if (this.userLocale) {
            return this.userLocale;
        }

        // Try to get from user settings (if store exists)
        try {
            const userStoreLocale = this.getLocaleFromUserStore();
            if (userStoreLocale) {
                this.userLocale = userStoreLocale;
                return this.userLocale;
            }
        } catch (e) {
            // Ignore errors from store
        }

        // Try to get from localStorage
        const storedLocale = localStorage.getItem('userLocale');
        if (storedLocale) {
            this.userLocale = storedLocale;
            return this.userLocale;
        }

        // Get from browser
        const browserLocale = this.getBrowserLocale();
        this.userLocale = browserLocale;
        return this.userLocale;
    }

    /**
     * Set user locale
     */
    setUserLocale(locale: string): void {
        this.userLocale = locale;
        // Save to localStorage
        localStorage.setItem('userLocale', locale);
    }

    /**
     * Get locale from browser
     */
    private getBrowserLocale(): string {
        // Get language from browser
        const browserLang = navigator.language || 'en';
        // Extract language code (first part)
        return browserLang.split('-')[0] || 'en';
    }

    /**
     * Get locale from user store (if available)
     */
    private getLocaleFromUserStore(): string | null {
        // Return null for now as we don't have a ready user settings store
        return null;
    }

    /**
     * Get supported locales
     */
    getSupportedLocales(): string[] {
        // Currently supporting only Russian and English
        return ['ru', 'en'];
    }

    /**
     * Check if locale is supported
     */
    isLocaleSupported(locale: string): boolean {
        const locales = this.getSupportedLocales();
        return locales.indexOf(locale) !== -1;
    }

    /**
     * Get fallback locale
     */
    getFallbackLocale(): string {
        return 'en'; // English as default fallback
    }

    /**
     * Get locale with fallback
     */
    getLocaleWithFallback(requestedLocale?: string): string {
        const locale = requestedLocale || this.getUserLocale();

        // If requested locale is supported, return it
        if (this.isLocaleSupported(locale)) {
            return locale;
        }

        // Otherwise return fallback
        return this.getFallbackLocale();
    }
}

// Export singleton service instance
export const localeService = new LocaleService();